/**
 * Theme utilities for AgroVet application
 * Manages theme switching, persistence, and detection
 */

export type Theme = 'light' | 'dark' | 'forest';

const THEME_STORAGE_KEY = 'agrovet-theme';
const THEME_ATTRIBUTE = 'data-theme';
const DARK_MODE_ATTRIBUTE = 'class';

/**
 * Get the current theme from DOM
 */
export function getCurrentTheme(): Theme {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'forest') {
    return stored;
  }

  const htmlElement = document.documentElement;
  if (htmlElement.classList.contains('dark')) {
    return 'dark';
  }
  if (htmlElement.classList.contains('forest')) {
    return 'forest';
  }

  return 'light';
}

/**
 * Set theme and persist to localStorage
 */
export function setTheme(theme: Theme): void {
  const htmlElement = document.documentElement;

  // Remove all theme classes
  htmlElement.classList.remove('dark', 'forest', 'light');

  // Add the new theme class (light is default, no class needed)
  if (theme === 'dark' || theme === 'forest') {
    htmlElement.classList.add(theme);
  }

  // Persist to localStorage
  localStorage.setItem(THEME_STORAGE_KEY, theme);

  // Dispatch custom event for theme change listeners
  window.dispatchEvent(
    new CustomEvent('theme-change', {
      detail: { theme },
    })
  );
}

/**
 * Toggle between light and dark themes
 */
export function toggleDarkMode(): Theme {
  const current = getCurrentTheme();
  const next = current === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

/**
 * Detect system dark mode preference
 */
export function getSystemThemePreference(): 'light' | 'dark' {
  if (window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Initialize theme on app load
 */
export function initializeTheme(): void {
  const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
  const theme = stored || getSystemThemePreference();
  setTheme(theme);
}

/**
 * Listen for theme changes
 */
export function onThemeChange(callback: (theme: Theme) => void): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ theme: Theme }>;
    callback(customEvent.detail.theme);
  };

  window.addEventListener('theme-change', handler);

  // Return unsubscribe function
  return () => {
    window.removeEventListener('theme-change', handler);
  };
}

/**
 * Get color token value by name
 */
export function getColorToken(tokenName: string): string {
  const root = document.documentElement;
  return getComputedStyle(root).getPropertyValue(`--${tokenName}`).trim();
}

/**
 * Get all available theme colors
 */
export function getThemeColors(): Record<string, string> {
  const root = document.documentElement;
  const style = getComputedStyle(root);

  return {
    background: style.getPropertyValue('--background'),
    foreground: style.getPropertyValue('--foreground'),
    primary: style.getPropertyValue('--primary'),
    secondary: style.getPropertyValue('--secondary'),
    accent: style.getPropertyValue('--accent'),
    destructive: style.getPropertyValue('--destructive'),
    muted: style.getPropertyValue('--muted'),
    border: style.getPropertyValue('--border'),
  };
}

/**
 * Detect if system uses dark mode
 */
export function prefersDarkMode(): boolean {
  return getSystemThemePreference() === 'dark';
}

/**
 * Get contrast color for text on background
 */
export function getContrastColor(backgroundColor: string): string {
  // Simple luminance calculation
  // This is a basic implementation; for production, use a more sophisticated algorithm
  const rgb = backgroundColor.match(/\d+/g);
  if (!rgb || rgb.length < 3) return '#000000';

  const r = parseInt(rgb[0] || '0', 10);
  const g = parseInt(rgb[1] || '0', 10);
  const b = parseInt(rgb[2] || '0', 10);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Apply theme-specific styles
 */
export function applyThemeOverride(
  selector: string,
  styles: Record<string, string>
): void {
  const element = document.querySelector(selector);
  if (!element) return;

  Object.entries(styles).forEach(([key, value]) => {
    (element as HTMLElement).style.setProperty(key, value);
  });
}

/**
 * Get available themes
 */
export function getAvailableThemes(): Array<{ value: Theme; label: string; icon?: string }> {
  return [
    { value: 'light', label: 'Light', icon: 'sun' },
    { value: 'dark', label: 'Dark', icon: 'moon' },
    { value: 'forest', label: 'Forest', icon: 'leaf' },
  ];
}
